import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    deleteDoc,
} from "firebase/firestore";
import {
    User,
    Shuffle,
    Filter,
    Edit3,
    Trash2,
    Save,
    X,
    Check,
    Clock,
    AlertCircle,
    Loader2
} from "lucide-react";
import EventLeaderboardUI from "./EventLeaderboardUI";

const EventLeaderboard = () => {
    const { uid } = useParams();
    const [runs, setRuns] = useState([]);
    const [allRuns, setAllRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [ownerData, setOwnerData] = useState({});
    const [isCompareOpen, setIsCompareOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [currentTrack, setCurrentTrack] = useState(0);
    const [tracks, setTracks] = useState([]);
    const [totalTimes, setTotalTimes] = useState([]);
    const [eventData, setEventData] = useState(null);

    // Estados para edición
    const [editMode, setEditMode] = useState(false);
    const [selectedRuns, setSelectedRuns] = useState(new Set());
    const [editingRun, setEditingRun] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [tempStartTime, setTempStartTime] = useState("");
    const [tempFinishTime, setTempFinishTime] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    // Cargar datos de propietarios
    const loadOwnerData = async (runsData, participantsData) => {
        const uniqueNfcIds = [...new Set(runsData.map((run) => run.NFCIdentifier))];
        const ownerPromises = uniqueNfcIds.map(async (nfcId) => {
            const tagsQuery = query(collection(db, "Tags"), where("NFCID", "==", nfcId));
            const tagsSnapshot = await getDocs(tagsQuery);

            if (!tagsSnapshot.empty) {
                const ownerUID = tagsSnapshot.docs[0].data().Owner;
                if (ownerUID === "") {
                    return { NFCIdentifier: nfcId, Owner: "Desconocido", PhotoUrl: "/default-avatar.jpg", categoryId: null };
                }
                const participant = participantsData.find((p) => p.userId === ownerUID);
                const categoryId = participant ? participant.categoryId : null;

                const userDocRef = doc(db, "Users", ownerUID);
                const userDoc = await getDoc(userDocRef);
                if (!userDoc.empty) {
                    const userData = userDoc.data();
                    return {
                        NFCIdentifier: nfcId,
                        Owner: userData.name,
                        PhotoUrl: userData.photoUrl || "/default-avatar.jpg",
                        categoryId: categoryId,
                    };
                }
            }

            const userDocRef = doc(db, "Users", nfcId);
            const userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                    NFCIdentifier: nfcId,
                    Owner: userData.name,
                    PhotoUrl: userData.photoUrl || "/default-avatar.jpg",
                    categoryId: participantsData.find(p => p.userId === nfcId)?.categoryId || null,
                };
            }

            return { NFCIdentifier: nfcId, Owner: "Desconocido", PhotoUrl: "/default-avatar.jpg", categoryId: null };
        });

        const ownerResults = await Promise.all(ownerPromises);
        return ownerResults.reduce((acc, ownerResult) => {
            acc[ownerResult.NFCIdentifier] = ownerResult;
            return acc;
        }, {});
    };

    // Parsear tiempo de Firebase
    const parseTimeString = (timeString) => {
        if (!timeString) return null;
        try {
            const [dateTimePart] = timeString.trim().split(' ');
            const [datePart, timePart] = dateTimePart.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes, secondsWithMs] = timePart.split(':');

            let seconds = secondsWithMs;
            let milliseconds = '000';
            if (secondsWithMs.includes('.')) {
                [seconds, milliseconds] = secondsWithMs.split('.');
                milliseconds = milliseconds.padEnd(3, '0');
            }

            const date = new Date(`${year}-${month}-${day}T${hours}:${minutes}:${seconds.padStart(2, '0')}.${milliseconds}`);
            return isNaN(date) ? null : date;
        } catch (error) {
            console.error("Error parsing time:", timeString, error);
            return null;
        }
    };

    const formatDateForInput = (date) => {
        if (!date) return '';
        return date.toISOString().slice(0, 16);
    };

    const formatDateToString = (date) => {
        if (!date || isNaN(date)) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds} UTC-7`;
    };

    const calculateElapsedTime = (startDate, finishDate) => {
        if (!startDate || !finishDate || finishDate <= startDate) return "DNF";

        const diffMs = finishDate - startDate;
        const hours = Math.floor(diffMs / 3600000);
        const minutes = Math.floor((diffMs % 3600000) / 60000);
        const seconds = Math.floor((diffMs % 60000) / 1000);
        const milliseconds = diffMs % 1000;

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    };

    const updateRun = async (runId, updates) => {
        try {
            const runRef = doc(db, "Runs", runId);
            await updateDoc(runRef, updates);
        } catch (error) {
            console.error("Error actualizando run:", error);
            throw error;
        }
    };

    const deleteRun = async (runId) => {
        try {
            await deleteDoc(doc(db, "Runs", runId));
        } catch (error) {
            console.error("Error eliminando run:", error);
            throw error;
        }
    };

    // Handlers
    const handleRunSelect = (runId) => {
        const newSelected = new Set(selectedRuns);
        if (newSelected.has(runId)) {
            newSelected.delete(runId);
        } else {
            newSelected.add(runId);
        }
        setSelectedRuns(newSelected);
    };

    const handleEditRun = (run) => {
        setEditingRun(run);
        const startDate = parseTimeString(run.StartTime);
        const finishDate = parseTimeString(run.FinishTime);
        setTempStartTime(formatDateForInput(startDate));
        setTempFinishTime(formatDateForInput(finishDate));
        setErrorMessage("");
        setEditDialogOpen(true);
    };

    const handleSaveEdits = async () => {
        if (!tempStartTime || !tempFinishTime) {
            setErrorMessage("Debes completar ambas horas");
            return;
        }

        const startDate = new Date(tempStartTime + ':00');
        const finishDate = new Date(tempFinishTime + ':00');

        if (finishDate <= startDate) {
            setErrorMessage("La hora de llegada debe ser posterior a la de salida");
            return;
        }

        const newElapsedTime = calculateElapsedTime(startDate, finishDate);
        const updates = {
            StartTime: formatDateToString(startDate),
            FinishTime: formatDateToString(finishDate),
            ElapsedTime: newElapsedTime,
        };

        try {
            await updateRun(editingRun.id, updates);
            setEditDialogOpen(false);
            setEditingRun(null);
            setTempStartTime("");
            setTempFinishTime("");
            setErrorMessage("");
        } catch (error) {
            setErrorMessage("Error guardando: " + error.message);
        }
    };

    const handleDeleteSelected = async () => {
        if (window.confirm(`¿Eliminar ${selectedRuns.size} carrera${selectedRuns.size !== 1 ? 's' : ''}?`)) {
            try {
                await Promise.all(Array.from(selectedRuns).map(runId => deleteRun(runId)));
                setSelectedRuns(new Set());
            } catch (error) {
                alert("Error eliminando: " + error.message);
            }
        }
    };

    // Cargar datos iniciales
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const eventRef = doc(db, "events", uid);
                const eventSnap = await getDoc(eventRef);
                if (eventSnap.exists()) setEventData(eventSnap.data());

                const categoriesRef = collection(db, `events/${uid}/categories`);
                const categoriesSnap = await getDocs(categoriesRef);
                setCategories(categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const participantsRef = collection(db, `events/${uid}/participants`);
                const participantsSnap = await getDocs(participantsRef);
                const participantsData = participantsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setParticipants(participantsData);

                const tracksRef = collection(db, `events/${uid}/tracks`);
                const tracksSnap = await getDocs(tracksRef);
                setTracks(tracksSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                setLoading(false);
            } catch (error) {
                console.error("Error loading data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, [uid]);

    const getTimeDifference = (time1, time2) => {
        if (!time1 || !time2 || time1 === "DNF" || time2 === "DNF") return null;

        const parseTime = (timeStr) => {
            const [h, m, s] = timeStr.split(':');
            const [seconds, ms] = s.split('.');
            return (parseInt(h) * 3600000) + (parseInt(m) * 60000) + (parseInt(seconds) * 1000) + parseInt(ms || '0');
        };

        const t1 = parseTime(time1);
        const t2 = parseTime(time2);
        return Math.abs(t2 - t1);
    };

    const getFilteredRuns = () => {
        // Lógica simplificada - implementa según tu estructura de datos
        return runs.filter(run =>
            selectedCategory === "all" ||
            ownerData[run.NFCIdentifier]?.categoryId === selectedCategory
        ).sort((a, b) => getTimeDifference("00:00:00.000", a.ElapsedTime) -
            getTimeDifference("00:00:00.000", b.ElapsedTime));
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="p-4 max-w-7xl mx-auto">
            {/* Edit Mode Toolbar */}
            <div className={`flex justify-between items-center mb-6 p-4 rounded-xl ${editMode
                ? 'bg-amber-50 border-2 border-amber-200'
                : 'bg-white'
                } shadow-sm`}>
                <label className="flex items-center gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={editMode}
                        onChange={(e) => setEditMode(e.target.checked)}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="font-semibold text-gray-800 flex items-center gap-2">
                        <Edit3 className="w-4 h-4" />
                        Modo Edición
                    </span>
                </label>

                {editMode && (
                    <div className="flex items-center gap-4">
                        <span className="text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                            {selectedRuns.size} seleccionada{selectedRuns.size !== 1 ? 's' : ''}
                        </span>
                        <button
                            onClick={handleDeleteSelected}
                            disabled={selectedRuns.size === 0}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 border-2 border-red-200 rounded-lg font-medium hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            <Trash2 className="w-4 h-4" />
                            Eliminar
                        </button>
                        <button
                            onClick={() => {
                                const selectedRun = runs.find(r => selectedRuns.has(r.id));
                                if (selectedRun) handleEditRun(selectedRun);
                            }}
                            disabled={selectedRuns.size !== 1}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                        >
                            <Edit3 className="w-4 h-4" />
                            Editar
                        </button>
                        <button
                            onClick={() => setEditMode(false)}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-900 transition-colors shadow-sm"
                        >
                            <X className="w-4 h-4" />
                            Salir
                        </button>
                    </div>
                )}
            </div>

            {/* Track Tabs */}
            <div className="border-b border-gray-200 mb-6">
                <div className="flex -mb-px">
                    <button
                        className={`${currentTrack === 0
                            ? 'border-b-2 border-black text-black'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } px-1 py-4 font-medium text-sm transition-colors`}
                        onClick={() => setCurrentTrack(0)}
                    >
                        Total
                    </button>
                    {tracks.slice(1).map((track, idx) => (
                        <button
                            key={track.id}
                            className={`${currentTrack === idx + 1
                                ? 'border-b-2 border-black text-black'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                } px-1 py-4 font-medium text-sm transition-colors`}
                            onClick={() => setCurrentTrack(idx + 1)}
                        >
                            {track.name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Filters & Compare Button */}
            <div className="flex justify-between items-center mb-8">
                <div className="relative">
                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
                    >
                        <option value="all">Todas las categorías</option>
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>

                {!editMode && (
                    <button
                        onClick={() => setIsCompareOpen(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-black text-white rounded-full font-semibold hover:bg-gray-800 transition-all duration-300 shadow-lg transform hover:-translate-y-0.5"
                    >
                        <User className="w-5 h-5" />
                        <Shuffle className="w-5 h-5" />
                        Comparate
                    </button>
                )}
            </div>

            {/* Edit Dialog */}
            {editDialogOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                    <Clock className="w-6 h-6 text-blue-600" />
                                    Editar Tiempo
                                </h2>
                                <button
                                    onClick={() => setEditDialogOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {errorMessage && (
                                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6 flex items-center gap-2">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                    <span>{errorMessage}</span>
                                </div>
                            )}

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Hora de Salida
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={tempStartTime}
                                        onChange={(e) => setTempStartTime(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Hora de Llegada
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={tempFinishTime}
                                        onChange={(e) => setTempFinishTime(e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>

                                {tempStartTime && tempFinishTime && (
                                    <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl">
                                        <span className="font-semibold">Tiempo calculado:</span>{' '}
                                        <span className="font-mono bg-white px-2 py-1 rounded text-sm">
                                            {calculateElapsedTime(
                                                new Date(tempStartTime + ':00'),
                                                new Date(tempFinishTime + ':00')
                                            )}
                                        </span>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-500 mb-2">
                                        Tiempo Original
                                    </label>
                                    <input
                                        type="text"
                                        value={editingRun?.ElapsedTime || ""}
                                        disabled
                                        className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-gray-500"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
                                <button
                                    onClick={() => setEditDialogOpen(false)}
                                    className="px-6 py-3 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSaveEdits}
                                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors shadow-sm"
                                >
                                    <Check className="w-4 h-4" />
                                    Guardar Cambios
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <EventLeaderboardUI
                runs={getFilteredRuns()}
                ownerData={ownerData}
                editMode={editMode}
                selectedRuns={selectedRuns}
                onRunSelect={handleRunSelect}
                onEditRun={handleEditRun}
                onDeleteRun={deleteRun}
                getTimeDifference={getTimeDifference}
            />

            {/* Compare Popup - implementa según necesites */}
            {isCompareOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    {/* Contenido del popup de comparación */}
                    <div className="bg-white p-6 rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto">
                        <h3 className="text-xl font-bold mb-4">Comparación</h3>
                        <button onClick={() => setIsCompareOpen(false)} className="absolute top-4 right-4">
                            <X className="w-6 h-6" />
                        </button>
                        {/* Aquí va el contenido de comparación */}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventLeaderboard;